/**
 * Deterministic "thin reasoning" heuristic for the GL WHY prompt (GL-1 §0,
 * GL-3 tranche brief scope 1). NO MODEL CALLS — this is a pure, synchronous
 * length + non-stopword-floor check, per the CRON zero-spend rider (the thin
 * check must be deterministic). It decides only whether a WHY response is
 * "adequate" or "thin"; it NEVER infers a misconception and NEVER leaves the
 * client (the WHY text goes ONLY to the GL-2 /gl/why surface — GL-1 §3).
 *
 * A response is ADEQUATE when it clears BOTH floors:
 *   - MIN_CHARS trimmed characters, AND
 *   - MIN_CONTENT_WORDS distinct non-stopword tokens (so "the the the the a a"
 *     stays thin — length alone can be gamed by filler).
 * Anything below either floor (including empty / whitespace) is THIN.
 *
 * The floors are conservative starting values; they carry no user text off the
 * device and are cheap to tune. Keep this module free of side effects so the
 * payload-schema and state-machine tests can exercise it in isolation.
 */
export const MIN_CHARS = 25;
export const MIN_CONTENT_WORDS = 4;

// A small, closed stopword set — enough that padding a response with filler
// words does not clear the content-word floor. Intentionally NOT exhaustive:
// the goal is "did the student express some substantive reasoning", not NLP.
export const STOPWORDS = new Set([
  "a", "an", "and", "are", "as", "at", "be", "because", "been", "but", "by",
  "do", "does", "for", "from", "had", "has", "have", "i", "if", "in", "is",
  "it", "its", "just", "me", "my", "no", "not", "of", "on", "or", "so",
  "that", "the", "then", "there", "they", "this", "to", "too", "was", "we",
  "were", "what", "when", "which", "will", "with", "would", "you", "your"
]);

/** Distinct lowercased alphanumeric tokens that are not stopwords. */
export const contentWords = (raw) => {
  const tokens = String(raw || "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  const distinct = new Set();
  for (const token of tokens) {
    if (!STOPWORDS.has(token)) {
      distinct.add(token);
    }
  }
  return distinct;
};

/**
 * @returns {boolean} true when the WHY response is THIN (below either floor).
 * Adequate = !isThinReasoning(...).
 */
export const isThinReasoning = (raw) => {
  const trimmed = String(raw || "").trim();
  if (trimmed.length < MIN_CHARS) {
    return true;
  }
  return contentWords(trimmed).size < MIN_CONTENT_WORDS;
};

/** Character length of the trimmed WHY — the ONLY WHY-derived value that may
 *  appear in an event payload (GL-1 §1 allowlist: whyLen, never the text). */
export const whyLength = (raw) => String(raw || "").trim().length;
